import { useState } from "react";
import ImageUpload from "./ImageUpload";
import StoryForm from "./StoryForm";
import CanvasRenderer from "./CanvasRenderer";
import DownloadButton from "./DownloadButton";
import { useAutoSave } from "../hooks/useAutoSave.js";
import SavedStatusIndicator from "./SavedStatusIndicator.jsx";
import RestorePrompt from "./RestorePrompt.jsx";

const INITIAL_STORY_DATA = {
  headline: "",
  subtitle: "",
  category: "",
  authorName: "",
  authorRole: "",
  backgroundImage: null,
  titlePosition: "top",
};

function GraphicsGenerator({ onStartOver }) {
  const [restoreDismissed, setRestoreDismissed] = useState(false);

  const {
    data: storyData,
    setData: setStoryData,
    clearSavedData,
    restoreSavedData,
    hasSavedData,
    isSaving,
    lastSaved,
    saveError,
  } = useAutoSave("pawPrint_graphics_formData", INITIAL_STORY_DATA);

  const handleUpdate = (updates) => {
    setStoryData((prev) => ({ ...prev, ...updates }));
  };

  const handleImageSelect = (imageUrl) => {
    handleUpdate({ backgroundImage: imageUrl });
  };

  const isFormComplete = () => {
    return (
      storyData.headline.trim() !== "" &&
      storyData.category.trim() !== "" &&
      storyData.authorName.trim() !== "" &&
      storyData.backgroundImage !== null
    );
  };

  const formComplete = isFormComplete();

  const getMissingFields = () => {
    const missing = [];
    if (!storyData.headline.trim()) missing.push("Headline");
    if (!storyData.category.trim()) missing.push("Category");
    if (!storyData.authorName.trim()) missing.push("Author Name");
    if (!storyData.backgroundImage) missing.push("Background Image");
    return missing;
  };

  return (
    <div className="graphics-generator-container">
      <div className="graphics-generator-header">
        <button type="button" onClick={onStartOver} className="start-over-btn">
          <i className="fas fa-redo"></i> Start Over
        </button>
      </div>

      <SavedStatusIndicator
        isSaving={isSaving}
        lastSaved={lastSaved}
        saveError={saveError}
        onClearSavedData={clearSavedData}
      />

      {hasSavedData && !restoreDismissed && (
        <RestorePrompt
          lastSaved={lastSaved}
          onResume={() => {
            restoreSavedData();
            setRestoreDismissed(true);
          }}
          onStartFresh={() => {
            clearSavedData();
            setRestoreDismissed(true);
          }}
        />
      )}

      <div className="graphics-main-container">
        <div className="graphics-left-panel">
          <ImageUpload
            onImageSelect={handleImageSelect}
            currentImage={storyData.backgroundImage}
          />
          <StoryForm storyData={storyData} onUpdate={handleUpdate} />
          {formComplete ? (
            <DownloadButton storyData={storyData} />
          ) : (
            <div className="form-incomplete-message">
              <p>
                <i className="fas fa-info-circle"></i> Please fill out all required fields to see preview and download:
              </p>
              <ul>
                {getMissingFields().map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="graphics-right-panel">
          <h3>Preview</h3>
          {formComplete ? (
            <CanvasRenderer storyData={storyData} />
          ) : (
            <div className="preview-placeholder">
              <p>
                <i className="fas fa-lock"></i> Complete the form to see preview
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GraphicsGenerator;
